import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function PwaInstallBanner() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('pwa-install-dismissed') === 'true'
  )

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setPromptEvent(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (!promptEvent || dismissed) return null

  const handleInstall = async () => {
    await promptEvent.prompt()
    const { outcome } = await promptEvent.userChoice
    if (outcome === 'accepted' || outcome === 'dismissed') {
      setPromptEvent(null)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', 'true')
    setDismissed(true)
  }

  return (
    <div className="fixed bottom-40 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-xl shadow-xl px-4 py-3 text-sm max-w-sm w-[calc(100vw-2rem)]">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center">
        <svg viewBox="0 0 32 32" className="w-5 h-5">
          <path d="M16 4 L30 28 L2 28 Z" fill="none" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
          <line x1="16" y1="13" x2="16" y2="21" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="16" cy="24.5" r="1.5" fill="white"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-900 dark:text-gray-100 font-medium leading-tight">Add to Home Screen</p>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Get instant access to live alerts</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleInstall}
          className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-medium rounded-lg transition-colors"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-1"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
